import { useEffect, useState } from "react";
import type { AgencyTeam } from "@nabla/api";
import { agencyCommands, agencyRead, type AgencyRead } from "../client/agency.ts";
import { designCopy } from "../design/copy.ts";
import type { Reply } from "../client/http.ts";
import { Button, Buttons } from "../design/primitives/Button.tsx";
import { Problem } from "../design/primitives/Field.tsx";
import { Rows } from "../design/primitives/List.tsx";
import { PageHeader } from "../design/primitives/Page.tsx";
import { Section } from "../design/primitives/Section.tsx";
import { Skeleton } from "../design/primitives/Skeleton.tsx";
import { InviteSheet, invited } from "../features/people/parts/InviteSheet.tsx"; import { peopleCopy } from "../features/people/copy.ts";
import { Failed } from "./App.tsx";
import { agencyCopy } from "./copy.ts";
import { replyProblem } from "./problem.ts";
import styles from "./agency.module.css";

const t = agencyCopy.equipe;
type Member = AgencyTeam["members"][number];

const invite: Invite = async (role, name, clients) => {
  const reply = await agencyCommands.invite(role, name, clients);
  return reply.kind === "feito" ? invited(reply.value, reply.value?.expiresAt) : { problem: replyProblem(reply) };
};

export function Team() {
  const [team, setTeam] = useState<AgencyRead<AgencyTeam> | undefined>(undefined);
  const [inviting, setInviting] = useState(false);
  const load = () => void agencyRead.equipe().then(setTeam);
  useEffect(load, []);
  const invites = team?.ok === true && team.value.invites;
  const action = invites ? <Button variant="primario" onClick={() => setInviting(true)}>{peopleCopy.convidar.botao}</Button> : undefined;
  const header = <PageHeader title={agencyCopy.lugares.equipe} intro={t.intro} action={action} />;
  if (team === undefined) return <>{header}<div aria-busy="true"><Skeleton height="240px" /></div></>;
  if (!team.ok) return <Failed onRetry={load} />;
  return (
    <>
      {header}
      <div className={styles.blocks}>
        <Section title={designCopy.lugares.pessoas} count={String(team.value.members.length)}>
          <Rows>
            {team.value.members.map((member) => <MemberRow key={member.id} member={member} team={team.value} onDone={load} />)}
          </Rows>
        </Section>
      </div>
      {invites ? <InviteSheet open={inviting} onClose={() => setInviting(false)} invite={invite} clients={grantable(team.value)} /> : null}
    </>
  );
}

const grantable = (team: AgencyTeam) => team.clients.filter((client) => client.administers).map(({ id, name }) => ({ id, name }));

function MemberRow({ member, team, onDone }: { readonly member: Member; readonly team: AgencyTeam; readonly onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | undefined>(undefined);
  const act = (sent: Promise<Reply<unknown>>, taking = false) => {
    setBusy(true);
    setProblem(undefined);
    void sent.then((reply) => {
      setBusy(false);
      if (reply.kind !== "feito") setProblem(replyProblem(reply, taking));
      onDone();
    });
  };
  const name = member.isMe ? `${member.name} (${peopleCopy.voce})` : member.name;
  const other = member.role === "admin" ? "gestor" : "admin";
  return (
    <li className={styles.member}>
      <h3 className={styles.clientName}>{name}</h3>
      <div className={styles.hold}>
        <span className={styles.small}>{t.naAgencia(t.papeis[member.role])}</span>
        {team.invites && !member.isMe ? (
          <Button onClick={() => act(agencyCommands.role(member.id, other))} disabled={busy}>{t.tornar(t.papeis[other])}</Button>
        ) : null}
      </div>
      <ul className={styles.holds}>
        {team.clients.map((client) => {
          const held = member.clients.find((c) => c.id === client.id);
          const pending = held === undefined && member.pending.includes(client.id);
          const role = held !== undefined ? t.papeis[held.role] : pending ? t.pendente : peopleCopy.semAcesso;
          const mine = client.administers && !(member.isMe && held !== undefined);
          return (
            <li key={client.id} className={styles.hold}>
              <span>{`${client.name} · ${role}`}</span>
              {!mine ? null : held !== undefined || pending ? (
                <Button onClick={() => act(agencyCommands.take(member.id, client.id), true)} disabled={busy}>
                  {pending ? t.cancelar : t.tirar}
                </Button>
              ) : (
                <Buttons>
                  {(["gestor", "admin"] as const).map((r) => (
                    <Button key={r} onClick={() => act(agencyCommands.give(member.id, client.id, r))} disabled={busy}>
                      {t.dar(t.papeis[r])}
                    </Button>
                  ))}
                </Buttons>
              )}
            </li>
          );
        })}
      </ul>
      {problem === undefined ? null : <Problem>{problem}</Problem>}
    </li>
  );
}
